import { adminApi, AdminApiError, currentCsrfToken } from './adminClient'

export interface AdminLoginInput {
  username: string
  password: string
}

export interface AdminStepUpInput {
  password: string
}

export interface AdminStepUpView {
  step_up_token: string
  expires_at: string
}

function validateAuthError(error: unknown): never {
  if (!(error instanceof AdminApiError)) throw error
  const details = error.details
  if (!details || typeof details !== 'object' || Array.isArray(details)) {
    throw new AdminApiError('ADMIN_UNEXPECTED_RESPONSE', error.status)
  }
  const record = details as Record<string, unknown>
  const keys = Object.keys(record)
  const valid = keys.every((key) => key === 'code' || key === 'trace_id') &&
    typeof record.code === 'string' &&
    (record.trace_id === undefined || typeof record.trace_id === 'string')
  if (!valid) throw new AdminApiError('ADMIN_UNEXPECTED_RESPONSE', error.status)
  throw error
}

function validateStepUpView(value: unknown): AdminStepUpView {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new AdminApiError('ADMIN_UNEXPECTED_RESPONSE', 200)
  }
  const record = value as Record<string, unknown>
  if (typeof record.step_up_token !== 'string' || typeof record.expires_at !== 'string') {
    throw new AdminApiError('ADMIN_UNEXPECTED_RESPONSE', 200)
  }
  return { step_up_token: record.step_up_token, expires_at: record.expires_at }
}

export const adminAuthApi = {
  async login(input: AdminLoginInput) {
    try {
      return await adminApi.request<void>('/admin/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify(input),
      }, undefined, 204, true)
    } catch (error) {
      validateAuthError(error)
    }
  },

  async logout() {
    try {
      return await adminApi.request<void>('/admin/v1/auth/logout', { method: 'POST' }, undefined, 204, true)
    } catch (error) {
      validateAuthError(error)
    }
  },

  async stepUp(input: AdminStepUpInput) {
    const csrf = currentCsrfToken()
    if (!csrf) throw new AdminApiError('ADMIN_CSRF_REQUIRED', 403)
    try {
      const value = await adminApi.request<AdminStepUpView>('/admin/v1/auth/step-up', {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrf },
        body: JSON.stringify(input),
      })
      return validateStepUpView(value)
    } catch (error) {
      validateAuthError(error)
    }
  },
}
